import { View, Text, Pressable, Platform } from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

/** Consistent section wrapper with icon, title, and intro text */
interface GuideSectionProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  color: string;
  intro?: string;
  children: React.ReactNode;
  id?: string;
}

export const GuideSection = ({ icon, title, color, intro, children, id }: GuideSectionProps) => (
  <View className="mb-12" {...(id ? { nativeID: id } : {})}>
    <View className="flex-row items-center gap-3 mb-4">
      <View
        className="w-12 h-12 rounded-xl items-center justify-center"
        style={{ backgroundColor: color + "15" }}
      >
        <Ionicons name={icon} size={24} color={color} />
      </View>
      <Text className="text-2xl font-bold text-gray-900">{title}</Text>
    </View>
    {intro && (
      <Text className="text-base text-gray-600 leading-6 mb-6">{intro}</Text>
    )}
    {children}
  </View>
);

/** Info card with icon and text */
interface GuideCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  color: string;
}

export const GuideCard = ({ icon, title, description, color }: GuideCardProps) => (
  <View className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-3">
    <View className="flex-row items-start gap-4">
      <View
        className="w-10 h-10 rounded-lg items-center justify-center"
        style={{ backgroundColor: color + "15" }}
      >
        <Ionicons name={icon} size={20} color={color} />
      </View>
      <View className="flex-1">
        <Text className="text-base font-semibold text-gray-900">{title}</Text>
        <Text className="text-sm text-gray-500 mt-1 leading-5">{description}</Text>
      </View>
    </View>
  </View>
);

/** Numbered step component for "how to" flows */
interface GuideStepProps {
  step: number;
  title: string;
  description: string;
  color?: string;
}

export const GuideStep = ({ step, title, description, color = "#7C3AED" }: GuideStepProps) => (
  <View className="flex-row items-start gap-4 mb-4">
    <View
      className="w-8 h-8 rounded-full items-center justify-center"
      style={{ backgroundColor: color }}
    >
      <Text className="text-sm font-bold text-white">{step}</Text>
    </View>
    <View className="flex-1">
      <Text className="text-base font-semibold text-gray-900">{title}</Text>
      <Text className="text-sm text-gray-500 mt-1 leading-5">{description}</Text>
    </View>
  </View>
);

/** Colored tip/info box */
interface GuideTipProps {
  icon?: keyof typeof Ionicons.glyphMap;
  title?: string;
  children: React.ReactNode;
  variant?: "info" | "warning" | "success";
}

const TIP_STYLES = {
  info: { bg: "bg-violet-50", border: "border-violet-200", iconColor: "#7C3AED", titleColor: "text-violet-900", textColor: "text-violet-700" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", iconColor: "#D97706", titleColor: "text-amber-900", textColor: "text-amber-700" },
  success: { bg: "bg-emerald-50", border: "border-emerald-200", iconColor: "#059669", titleColor: "text-emerald-900", textColor: "text-emerald-700" },
};

export const GuideTip = ({ icon = "bulb", title, children, variant = "info" }: GuideTipProps) => {
  const styles = TIP_STYLES[variant];
  return (
    <View className={`${styles.bg} rounded-xl p-5 border ${styles.border} mb-4`}>
      <View className="flex-row items-start gap-3">
        <Ionicons name={icon} size={22} color={styles.iconColor} />
        <View className="flex-1">
          {title && (
            <Text className={`text-sm font-semibold ${styles.titleColor} mb-1`}>{title}</Text>
          )}
          <Text className={`text-sm ${styles.textColor} leading-5`}>{children}</Text>
        </View>
      </View>
    </View>
  );
};

/** Game detail card */
interface GuideGameCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  name: string;
  description: string;
  modes: string[];
  color: string;
  howToPlay: string;
}

export const GuideGameCard = ({ icon, name, description, modes, color, howToPlay }: GuideGameCardProps) => (
  <View className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-4">
    <View className="flex-row items-center gap-3 mb-3">
      <View
        className="w-12 h-12 rounded-xl items-center justify-center"
        style={{ backgroundColor: color + "15" }}
      >
        <Ionicons name={icon} size={24} color={color} />
      </View>
      <View className="flex-1">
        <Text className="text-lg font-bold text-gray-900">{name}</Text>
        <View className="flex-row flex-wrap gap-2 mt-1">
          {modes.map((mode) => (
            <View key={mode} className="px-2 py-0.5 rounded-full" style={{ backgroundColor: color + "15" }}>
              <Text className="text-xs font-medium" style={{ color }}>{mode}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
    <Text className="text-sm text-gray-600 leading-5 mb-3">{description}</Text>
    <View className="bg-gray-50 rounded-lg p-3">
      <Text className="text-xs font-semibold text-gray-500 uppercase mb-1">Hoe speel je?</Text>
      <Text className="text-sm text-gray-600 leading-5">{howToPlay}</Text>
    </View>
  </View>
);

/** Next/previous page navigation */
interface GuideNavProps {
  previous?: { href: string; label: string };
  next?: { href: string; label: string };
}

export const GuideNav = ({ previous, next }: GuideNavProps) => (
  <View className="flex-row justify-between mt-12 pt-6 border-t border-gray-200">
    {previous ? (
      <Link href={previous.href as any} asChild>
        <Pressable className="flex-row items-center gap-2" style={{ cursor: "pointer" } as any}>
          <Ionicons name="arrow-back" size={18} color="#7C3AED" />
          <View>
            <Text className="text-xs text-gray-400">Vorige</Text>
            <Text className="text-sm font-semibold text-violet-700">{previous.label}</Text>
          </View>
        </Pressable>
      </Link>
    ) : (
      <View />
    )}
    {next ? (
      <Link href={next.href as any} asChild>
        <Pressable className="flex-row items-center gap-2" style={{ cursor: "pointer" } as any}>
          <View className="items-end">
            <Text className="text-xs text-gray-400">Volgende</Text>
            <Text className="text-sm font-semibold text-violet-700">{next.label}</Text>
          </View>
          <Ionicons name="arrow-forward" size={18} color="#7C3AED" />
        </Pressable>
      </Link>
    ) : (
      <View />
    )}
  </View>
);

/** Print / PDF download button */
export const PrintButton = () => {
  if (Platform.OS !== "web") return null;

  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  return (
    <Pressable
      onPress={handlePrint}
      className="flex-row items-center gap-2 px-5 py-3 rounded-xl bg-violet-600 hover:bg-violet-700 active:bg-violet-800 transition-colors"
      style={{ cursor: "pointer" } as any}
    >
      <Ionicons name="download-outline" size={20} color="#FFFFFF" />
      <Text className="text-base font-semibold text-white">Opslaan als PDF</Text>
    </Pressable>
  );
};

/** Page header for individual guide pages */
interface GuidePageHeaderProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  subtitle: string;
  color: string;
}

export const GuidePageHeader = ({ icon, title, subtitle, color }: GuidePageHeaderProps) => (
  <View className="mb-8">
    <View className="flex-row items-center gap-4 mb-3">
      <View
        className="w-14 h-14 rounded-2xl items-center justify-center"
        style={{ backgroundColor: color + "15" }}
      >
        <Ionicons name={icon} size={28} color={color} />
      </View>
      <Text className="text-3xl font-bold text-gray-900">{title}</Text>
    </View>
    <Text className="text-lg text-gray-500 leading-7">{subtitle}</Text>
  </View>
);

/** Ordered list of guide pages for navigation */
export const GUIDE_PAGES = [
  { href: "/(web)/guide/getting-started", label: "Aan de Slag", icon: "rocket" as const, color: "#7C3AED" },
  { href: "/(web)/guide/weekend-mode", label: "Familie Weekend", icon: "calendar" as const, color: "#3B82F6" },
  { href: "/(web)/guide/roles", label: "Rollen", icon: "shield" as const, color: "#D97706" },
  { href: "/(web)/guide/teams", label: "Teams", icon: "people" as const, color: "#EC4899" },
  { href: "/(web)/guide/agenda", label: "Agenda", icon: "today" as const, color: "#10B981" },
  { href: "/(web)/guide/chat", label: "Chat", icon: "chatbubbles" as const, color: "#6366F1" },
  { href: "/(web)/guide/games", label: "Spellen", icon: "game-controller" as const, color: "#EF4444" },
  { href: "/(web)/guide/challenges", label: "Uitdagingen", icon: "flash" as const, color: "#F59E0B" },
  { href: "/(web)/guide/points", label: "Punten", icon: "trophy" as const, color: "#F59E0B" },
  { href: "/(web)/guide/speurtocht", label: "Speurtocht", icon: "map" as const, color: "#06B6D4" },
  { href: "/(web)/guide/notifications", label: "Meldingen", icon: "notifications" as const, color: "#8B5CF6" },
  { href: "/(web)/guide/photos", label: "Foto's", icon: "images" as const, color: "#EC4899" },
];

/** Helper to get prev/next navigation for a given page href */
export const getGuideNav = (currentHref: string) => {
  const index = GUIDE_PAGES.findIndex((p) => p.href === currentHref);
  return {
    previous: index > 0 ? { href: GUIDE_PAGES[index - 1].href, label: GUIDE_PAGES[index - 1].label } : undefined,
    next: index < GUIDE_PAGES.length - 1 ? { href: GUIDE_PAGES[index + 1].href, label: GUIDE_PAGES[index + 1].label } : undefined,
  };
};
